"use client";

import Image from "next/image";
import React, { useEffect, useState } from "react";
import { LocalizedLink } from "@/components/i18n/LocalizedLink";
import CompanyLogo from "@/components/shared/CompanyLogo";
import ListingImagePlaceholder from "@/features/listings/components/ListingImagePlaceholder";
import { Heart } from "@/components/icons";
import { useAuth } from "@/context/AuthContext";
import { canUseFavorites } from "@/features/listings/hooks/useListings";
import { useI18n } from "@/i18n/I18nProvider";
import {
  formatLocalizedAmount,
  formatLocalizedCurrency,
  localizedText,
} from "@/i18n/text";

// ÄNDRING: Vi definierar props manuellt istället för att ärva från gamla ListingWithRelations
export type ListingCardSmallProps = {
  title: string;
  /** Visas som den kommer från backend, som redan formaterar område/stad. */
  location?: string | null;
  rooms: number;
  sizeM2: number;
  rent: number;
  /**
   * The ordinary rent, struck through beside `rent`, when a campaign has
   * restated the price. The card stays free of campaign types on purpose —
   * callers that hold the DTO resolve the two figures and pass them down.
   */
  originalRent?: number | null;
  /** One line qualifying the campaign price, e.g. "Rabatterat pris i 3 mån". */
  priceNote?: string | null;
  imageUrl?: string;      // En enkel sträng nu (URL)
  /** Load the photo eagerly with high priority — for the first cards above the fold. */
  imagePriority?: boolean;
  landlordType?: string;  // Motsvarar hostType ("Företag")
  hostName?: string;
  hostLogoUrl?: string;
  isVerified?: boolean;
  
  // NEW: Favoritfunktion (hjärta)
  id?: string;
  isFavorite?: boolean;
  onFavoriteToggle?: (id: string, isFav: boolean) => void;
  
  // Funktioner & UI
  /**
   * Where the card leads, unlocalized ("/housing/<id>"). Renders a real link
   * over the whole card so keyboard, screen readers, new-tab and crawlers work;
   * prefer it over `onClick`, which stays for cards that are not navigation.
   */
  href?: string;
  onClick?: () => void;
  onHoverChange?: (hovering: boolean) => void;
  variant?: "default" | "compact";
  footerContent?: React.ReactNode;
  showFavoriteButton?: boolean;
  imageOverlayContent?: React.ReactNode;
  /** Top-left of the photo. Kept clear for the campaign mark. */
  imageTopLeftContent?: React.ReactNode;
  imageTopRightContent?: React.ReactNode;
  /**
   * Sits over the bottom-left of the image. Its own slot rather than part of
   * `imageOverlayContent` so callers can use both, and so it stays clickable —
   * the overlay is deliberately pointer-events-none.
   */
  imageBottomLeftContent?: React.ReactNode;
  showHostLogo?: boolean;
  contentTopRightContent?: React.ReactNode;
};

const BASE_WIDTH = 470;
const IMAGE_ASPECT_RATIO = "16 / 10";

/** Design px → rem, so the card follows the reader's font-size setting. */
const rem = (px: number) => `${px / 16}rem`;

const formatRent = (rent: number | null | undefined, locale: "sv" | "en") =>
  typeof rent === "number" ? formatLocalizedCurrency(locale, rent) : "-";

const ListingCardSmall: React.FC<ListingCardSmallProps> = (props) => {
  const {
    title,
    location,
    rooms,
    sizeM2,
    rent,
    originalRent,
    priceNote,
    landlordType,
    hostName,
    hostLogoUrl,
    id,
    isFavorite,
    onFavoriteToggle,
    imageUrl,
    href,
    onClick,
    onHoverChange,
    variant = "default",
    imagePriority = false,
    footerContent,
    showFavoriteButton = true,
    imageOverlayContent,
    imageTopLeftContent,
    imageTopRightContent,
    imageBottomLeftContent,
    showHostLogo = true,
    contentTopRightContent,
  } = props;

  const { user } = useAuth();
  const { locale } = useI18n();

  const [isLiked, setIsLiked] = useState(isFavorite || false);
  // Trasiga bild-URL:er (utgångna blob-länkar m.m.) ska ge platshållaren,
  // inte webbläsarens trasig-bild-ikon.
  const [brokenImageUrl, setBrokenImageUrl] = useState<string | null>(null);
  const resolvedImageUrl =
    imageUrl && imageUrl !== brokenImageUrl ? imageUrl : undefined;
  
  useEffect(() => {
    if (isFavorite !== undefined) {
      setIsLiked(isFavorite);
    }
  }, [isFavorite]);
  
  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newLikedState = !isLiked;
    if (!user && id && onFavoriteToggle) {
      onFavoriteToggle(id, newLikedState);
      return;
    }

    setIsLiked(newLikedState);
    if (id && onFavoriteToggle) {
      onFavoriteToggle(id, newLikedState);
    }
  };

  const maxWidth = variant === "compact" ? 360 : BASE_WIDTH;
  // Was a per-card ResizeObserver shrinking everything to 86%, which pushed
  // meta text to ~10px. Fixed sizes with a 12px floor read better and stop the
  // re-render every card did on resize.
  const scaleValue = rem;
  const isCompact = variant === "compact";
  const locationText =
    location?.trim() || localizedText(locale, "Ej angivet", "Not specified");
  const detailsText = `${rooms ?? "-"} ${localizedText(locale, "rum", "rooms")} \u00b7 ${sizeM2 ?? "-"} m\u00b2`;
  const shouldShowHostLogo = showHostLogo && Boolean(hostLogoUrl);
  const logoSize = variant === "compact" ? 60 : 76;
  const contentPadding = isCompact ? 12 : 14;
  const logoRightOffset = shouldShowHostLogo ? 14 : 0;
  const hasContentTopRight = Boolean(contentTopRightContent);
  const contentRightPadding = shouldShowHostLogo
    ? logoRightOffset + logoSize + 14
    : hasContentTopRight
      ? contentPadding + 96
      : contentPadding;
  const logoAlt = hostName || landlordType
    ? `${hostName ?? landlordType} ${localizedText(locale, "logotyp", "logo")}`
    : localizedText(locale, "Hyresvärdens logotyp", "Landlord logo");
  const showStudentFavoriteButton = showFavoriteButton && canUseFavorites(user);
  // Only strike a price that is genuinely higher than what is being asked;
  // an equal "was" price would read as a discount that is not one.
  const hasCampaignPrice =
    typeof originalRent === "number" &&
    Number.isFinite(originalRent) &&
    originalRent > rent;

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => onHoverChange?.(true)}
      onMouseLeave={() => onHoverChange?.(false)}
      className="group relative flex w-full cursor-pointer flex-col border border-black/[0.08] bg-white shadow-[0_8px_20px_rgba(15,23,42,0.08)] transition-shadow duration-200 hover:shadow-[0_12px_26px_rgba(15,23,42,0.12)]"
      style={{
        maxWidth,
        minWidth: 0,
        borderRadius: scaleValue(28),
        overflow: "hidden"
      }}
    >
      {/* Covers the whole card under the controls (z-10+), so the heart and
          badges stay their own targets. The focus ring is inset because the
          card's overflow clip would cut an outer one. */}
      {href && (
        <LocalizedLink
          href={href}
          className="absolute inset-0 z-5 rounded-[inherit] focus-visible:outline-3 focus-visible:-outline-offset-3 focus-visible:outline-brand"
        >
          <span className="sr-only">{title}</span>
        </LocalizedLink>
      )}

      {/* IMAGE */}
      <div
        className="w-full bg-gray-100 overflow-hidden relative shrink-0 group/image"
        style={{
          aspectRatio: IMAGE_ASPECT_RATIO,
          lineHeight: 0,
          minHeight: 0,
        }}
      >
        {imageTopLeftContent && (
          <div className="absolute left-3 top-3 z-20 max-w-[calc(100%-1.5rem)]">
            {imageTopLeftContent}
          </div>
        )}

        {/* Shares its corner with the favourite heart below, so only one of the
            two is ever in play on a given card. */}
        {imageTopRightContent && (
          <div className="absolute right-3 top-3 z-10">{imageTopRightContent}</div>
        )}

        {imageOverlayContent && (
          <div className="pointer-events-none absolute inset-0 z-20">
            {imageOverlayContent}
          </div>
        )}

        {imageBottomLeftContent && (
          <div className="absolute bottom-3 left-3 z-20 max-w-[calc(100%-1.5rem)]">
            {imageBottomLeftContent}
          </div>
        )}

        {showStudentFavoriteButton && (
          <button
            type="button"
            onClick={handleFavoriteClick}
            className="absolute right-3 top-3 z-10 rounded-full bg-white/95 p-2.5 shadow-sm backdrop-blur-sm transition-all hover:scale-105 active:scale-95"
            aria-label={
              isLiked
                ? localizedText(locale, "Ta bort från sparade", "Remove from saved")
                : localizedText(locale, "Spara bostad", "Save home")
            }
          >
            <Heart className={`h-5 w-5 transition-colors ${isLiked ? 'fill-red-500 text-red-500' : 'text-gray-500'}`} />
          </button>
        )}

        {resolvedImageUrl ? (
          <Image
            src={resolvedImageUrl}
            alt={title}
            fill
            priority={imagePriority}
            sizes={
              variant === "compact"
                ? "(max-width: 640px) 86vw, 360px"
                : "(max-width: 768px) 92vw, (max-width: 1280px) 45vw, 470px"
            }
            className="absolute inset-0 block h-full w-full object-cover object-center transition-transform duration-500"
            onError={() => setBrokenImageUrl(resolvedImageUrl)}
          />
        ) : (
          <ListingImagePlaceholder className="absolute inset-0" />
        )}
      </div>

      {/* CONTENT */}
      <div
        className="relative flex flex-col overflow-hidden"
        style={{
          padding: scaleValue(contentPadding),
          paddingRight: scaleValue(contentRightPadding),
          gap: scaleValue(isCompact ? 7 : 8),
        }}
      >
        {contentTopRightContent && (
          <div
            className="absolute z-[1]"
            style={{
              top: scaleValue(contentPadding),
              right: scaleValue(contentPadding),
            }}
          >
            {contentTopRightContent}
          </div>
        )}

        {shouldShowHostLogo && (
          <div
            className="absolute top-1/2 flex items-center justify-center"
            style={{
              right: scaleValue(logoRightOffset),
              width: scaleValue(logoSize),
              height: scaleValue(logoSize),
              borderRadius: scaleValue(6),
              overflow: "hidden",
              transform: "translateY(-50%)",
            }}
          >
            <CompanyLogo
              src={hostLogoUrl}
              alt={logoAlt}
              name={hostName ?? landlordType}
              className="h-full w-full bg-white ring-1 ring-black/5"
              imageClassName="p-0"
              style={{ borderRadius: scaleValue(6) }}
            />
          </div>
        )}

        {/* Listing facts + host logo */}
        <div
          className="min-w-0 overflow-hidden"
        >
          <div
            className="min-w-0"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: scaleValue(isCompact ? 2 : 3),
            }}
          >
            <p
              className="font-semibold text-[#6f6f6f]"
              style={{
                fontSize: scaleValue(12),
                lineHeight: scaleValue(16),
                display: "-webkit-box",
                WebkitLineClamp: 1,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                wordBreak: "break-word",
              }}
            >
              {locationText}
            </p>

            <h3
              className="font-normal text-[#111111]"
              style={{
                fontSize: scaleValue(isCompact ? 15.5 : 17),
                lineHeight: scaleValue(isCompact ? 20 : 22),
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                wordBreak: "break-word",
              }}
            >
              {title}
            </h3>

            <div
              className="flex flex-wrap items-baseline"
              style={{
                marginTop: scaleValue(isCompact ? 1 : 2),
                columnGap: scaleValue(6),
              }}
            >
              <span
                className={`text-[#202020] ${hasCampaignPrice ? "font-semibold" : "font-normal"}`}
                style={{
                  fontSize: scaleValue(isCompact ? 14 : 15),
                  lineHeight: scaleValue(isCompact ? 18 : 20),
                }}
              >
                {formatRent(rent, locale)}
              </span>

              {hasCampaignPrice && (
                <span
                  className="font-normal text-[#6f6f6f] line-through"
                  style={{
                    fontSize: scaleValue(isCompact ? 12 : 13),
                    lineHeight: scaleValue(isCompact ? 16 : 18),
                  }}
                >
                  {formatLocalizedAmount(locale, originalRent as number)}
                </span>
              )}
            </div>

            {priceNote && (
              <p
                className="font-normal text-[#6f6f6f]"
                style={{
                  marginTop: scaleValue(1),
                  fontSize: scaleValue(12),
                  lineHeight: scaleValue(16),
                  wordBreak: "break-word",
                }}
              >
                {priceNote}
              </p>
            )}

            <p
              className="font-normal text-[#6f6f6f]"
              style={{
                fontSize: scaleValue(12),
                lineHeight: scaleValue(16),
                wordBreak: "break-word",
              }}
            >
              {detailsText}
            </p>
          </div>
        </div>

        {footerContent && (
          <div
            className="relative z-10 border-t border-gray-200 pt-2"
            style={{ marginTop: scaleValue(2) }}
          >
            {footerContent}
          </div>
        )}
      </div>
    </div>
  );
};

export default ListingCardSmall;
